import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Spin } from 'antd';
import { Colors, Modal, PrimaryButton, device, GreenButton, isMobile } from 'utils';
import { Layout, PaymentCard } from 'components';
import { InfoForm } from './components/PersonalInfo/InfoForm';
import Donation from './components/Donation'
import { Link, Redirect, withRouter } from 'react-router-dom';
import '../components/PaymentPageStyle.css';
import { ProjectsActions } from 'redux/actions';
import { projectsServices } from 'services';
import { ArrowLeftOutlined } from '@ant-design/icons';
import BismillahIcon from './Icons/Bismillah';
import BismillahMobileIcon from './Icons/BismillahMobile';
import moment from 'moment/moment';
import AvatarBox from '../../Project/v2/components/DetailComponents/AvatarBox';


const islamicMonths = {
  '01': 'Muharram',
  '02': 'Safar',
  '03': 'Rabi al-Awwal',
  '04': 'Rabi al-Thani',
  '05': 'Jumada al-Awwal',
  '06': 'Jumada al-Thani',
  '07': 'Rajab',
  '08': "Sha'ban",
  '09': 'Ramadan',
  10: 'Shawwal',
  11: 'Dhuʻl-Qiʻdah',
  12: 'Dhu al-Hijjah',
};

const getIslamicDate = () => {
  const today = new Date();

  const gregorian = today.toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });

  const hijriParts = new Intl.DateTimeFormat('en-SA-u-ca-islamic-umalqura', {
    day: 'numeric',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(today);

  const day = hijriParts.find((p) => p.type === 'day')?.value;
  const month = hijriParts.find((p) => p.type === 'month')?.value;
  const year = hijriParts.find((p) => p.type === 'year')?.value;

  return {
    gregorian,
    hijri: `${day} ${islamicMonths[month] || month} ${year}`,
  };
};

const isProjectId = (value) => /^\d+$/.test(String(value || '').trim());

const getResolvedProjectUrl = async (projectKey) => {
  if (!projectKey) return null;

  const normalizedProjectKey = String(projectKey).trim();

  if (!isProjectId(normalizedProjectKey)) {
    return normalizedProjectKey;
  }

  const response = await projectsServices.getProjectUrl(normalizedProjectKey);

  return (
    response?.url ||
    response?.project?.url ||
    response?.data?.url ||
    null
  );
};

const getDonationFrequencyLabel = (donationType) => {
  const labels = {
    0: '(Einmalig)',
    1: '(Täglich)',
    2: '(Wöchentlich)',
    3: '(Monatlich)',
    4: '(Wöchentlich)',
    5: '(Monatlich)',
  };

  return labels[Number(donationType)] || labels[0];
};

const hasDisplayValue = (value) => {
  if (value == null) return false;

  const normalizedValue = String(value).trim().toLowerCase();
  return (
    normalizedValue !== '' &&
    normalizedValue !== 'undefined' &&
    normalizedValue !== 'null'
  );
};

const formatEuroAmount = (value) =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const DankeModal = styled.div`
  text-align: center;
  padding: 20px 25px;

  @media ${device.tablet} {
    min-width: 500px;
  }

  button {
    margin-top: 25px;
  }
`;

function DonationV2Page(props) {
  const {
    showProject,
    updateDraft,
    location,
    history,
    donationSuccess,
    project: storedProject,
    match,
    donationType,
  } = props;

  const project = props.projectOverride || storedProject;
  const isFridayCheckout = props.projectOverride?.title === 'freitag';

  const initialDonation = parseFloat(
    new URLSearchParams(location.search).get('donation') || 0
  ).toFixed(2);
  const initialQueryParams = new URLSearchParams(location.search || '');
  const isPaymentReturn =
    initialQueryParams.get('paymentId') != null ||
    initialQueryParams.get('payment_intent') != null;



  const [url, setUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [currentView, setCurrentView] = useState(null);
  const [showOption, setShowOption] = useState(false);
  const [otherOption, setOtherOption] = useState(false);
  const [donation, setDonation] = useState(initialDonation);
  const [freiwilliges, setFreiwilliges] = useState(0);
  const [allRewards, setAllRewards] = useState([]);
  const [selectedRewards, setSelectedRewards] = useState([]);
  const [addressRequired, setAddressRequiredState] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [checkoutProcessing, setCheckoutProcessing] =
    useState(isPaymentReturn);

  const donationFormRef = useRef(null);

  const supportSectionRef = useRef(null);
  const infoSectionRef = useRef(null);
  const paymentSectionRef = useRef(null);
  const [supportLevelSelected, setSupportLevelSelected] = useState(null);
  const getRewardTotalPrice = (item) =>
    Number(item?.price || 0) * Number(item?.quantity || 1);
  const handleClientSecret = (value) => {
    setClientSecret(value);
  };


  useEffect(() => {
    window.scroll({
      top:0,
      left:0
    })
  }, [project]);
  const handelCloseModal = () => {
    setShowModal(false);
    document.body.style.overflowY = 'unset';
  };

  const handelShowModal = () => {
    setShowModal(true);
    document.body.style.overflowY = 'hidden';
  };

  const updateDonationUrl = (nextDonation, nextFreiwilliges) => {
    if (!history || !location) return;

    const params = new URLSearchParams(location.search || '');

    if (nextDonation != null && nextDonation !== '') {
      params.set('donation', String(nextDonation));
    }

    if (nextFreiwilliges != null && nextFreiwilliges !== '') {
      params.set('tips', String(nextFreiwilliges));
    }

    const newSearch = params.toString();

    if (newSearch !== (location.search || '').replace(/^\?/, '')) {
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  };

  const setDonationState = (value) => {
    setDonation(value);
    updateDonationUrl(value, freiwilliges);
  };

  const setFreiwilligesState = (value) => {
    setFreiwilliges(value);
    updateDonationUrl(donation, value);
  };

  const setAddressRequired = (rewards) => {
    let isAddressRequired = false;

    rewards.forEach((reward) => {
      isAddressRequired = isAddressRequired || reward.address_required;
    });

    setAddressRequiredState(isAddressRequired);
  };


  const routeProjectKey =
    initialQueryParams.get('project_url') ??
    initialQueryParams.get('url') ??
    initialQueryParams.get('id') ??
    match?.params?.id ??
    null;

  const fetchedProjectRef = useRef(null);
  const routeMatchesCurrentProject =
    routeProjectKey != null &&
    String(project?.id) === String(routeProjectKey) &&
    hasDisplayValue(project?.title);

  useEffect(() => {
    if (isFridayCheckout || !routeProjectKey) return;

    if (routeMatchesCurrentProject) {
      setUrl(project.url || String(routeProjectKey));
      fetchedProjectRef.current = project.url || String(routeProjectKey);
      return;
    }

    let isMounted = true;

    const fetchProject = async () => {
      const resolvedProjectUrl = await getResolvedProjectUrl(routeProjectKey);

      if (!isMounted || !resolvedProjectUrl) return;

      if (fetchedProjectRef.current === resolvedProjectUrl) return;

      fetchedProjectRef.current = resolvedProjectUrl;

      setUrl(resolvedProjectUrl);

      showProject(resolvedProjectUrl);
    };

    fetchProject().catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [
    isFridayCheckout,
    project.url,
    routeMatchesCurrentProject,
    routeProjectKey,
    showProject,
  ]);

  // Redux may still contain the previously viewed project during the first
  // render. Only expose project-dependent UI once the requested URL and the
  // loaded project match.
  const isProjectReady =
    isFridayCheckout ||
    (routeProjectKey
      ? routeMatchesCurrentProject || Boolean(
          url && project?.url === url && hasDisplayValue(project?.title)
        )
      : hasDisplayValue(project?.title));

  useEffect(() => {
    const sections = [
      {
        key: 'support',
        ref: supportSectionRef,
      },
      {
        key: 'info',
        ref: infoSectionRef,
      },
      {
        key: 'payment',
        ref: paymentSectionRef,
      },
    ];

    const visibleRatios = {};

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const key = entry.target.getAttribute('data-checkout-section');

          visibleRatios[key] = entry.isIntersecting
            ? entry.intersectionRatio
            : 0;
        });

        const mostVisibleSection = Object.entries(visibleRatios).sort(
          (a, b) => b[1] - a[1]
        )[0];

        if (mostVisibleSection && mostVisibleSection[1] > 0) {
          setCurrentView(mostVisibleSection[0]);
        }
      },
      {
        root: null,
        threshold: [0.15, 0.25, 0.4, 0.6, 0.8],
        rootMargin: '-120px 0px -45% 0px',
      }
    );

    sections.forEach(({ ref, key }) => {
      if (ref.current) {
        ref.current.setAttribute('data-checkout-section', key);
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [isProjectReady, showPaymentForm]);

  // useEffect(() => {
  //   const {
  //     params: { url: routeUrl, id },
  //   } = match;
  //
  //   const storedProjectUrl = localStorage.getItem('project_url');
  //   const paramFromRoute = routeUrl ?? id;
  //
  //   showProject(storedProjectUrl || paramFromRoute || null);
  //   setUrl(paramFromRoute ?? storedProjectUrl);
  // }, [match, location.pathname, showProject]);
  //
  // useEffect(() => {
  //   const nextUrl = match?.params?.url;
  //
  //   if (url && url !== nextUrl) {
  //     showProject(nextUrl);
  //     setUrl(nextUrl);
  //   }
  // }, [match?.params?.url, showProject, url]);

  if (!isProjectReady) {
    return null;
  }

  const { search, state } = location;

  const id = isFridayCheckout ? project.id : match.params.id;

  const originalDonation = Number(
    new URLSearchParams(search).get('donation') || 0
  );

  const originalRewardId = Number(
    new URLSearchParams(search).get('rewardId') || 0
  );


  const hasSupportLevels =
    Array.isArray(project?.support_levels) &&
    project.support_levels.length > 0 &&
    !project?.is_ticketable;

  const hasRewardStep =
    selectedRewards?.length > 0 ||
    allRewards?.length > 0 ||
    project?.is_ticketable;

  const hasRhythmusStep = !!project?.recurring_check;

  const checkoutSteps = [
    'support',
    ...(hasSupportLevels ? ['support_levels'] : []),
    ...(hasRhythmusStep ? ['rhythmus'] : []),
    ...(hasRewardStep ? ['reward'] : []),
    'info',
    ...(showPaymentForm ? ['payment'] : []),
  ];

  const supportAmount = Number(donation || 0);
  const donationFrequencyLabel = getDonationFrequencyLabel(donationType);
  const hasSelectedRewards = selectedRewards?.length > 0;
  const supportDone = project?.is_ticketable
    ? hasSelectedRewards
    : supportAmount > 0 || supportLevelSelected || hasSelectedRewards;

  const stepDoneMap = {
    support: supportDone,
    support_levels: supportLevelSelected,
    rhythmus: hasRhythmusStep,
    reward: hasSelectedRewards,
    info: showPaymentForm,
    payment: false,
  };

  const supportScopedSteps = checkoutSteps.filter(
    (step) => !['info', 'payment'].includes(step)
  );

  const activeSupportStep =
    supportScopedSteps.find((step) => !stepDoneMap[step]) ||
    supportScopedSteps[supportScopedSteps.length - 1] ||
    'support';

  const resolvedCurrentStep =
    (currentView || 'support') === 'support'
      ? activeSupportStep
      : currentView || 'support';

  const resolvedCurrentStepIndex = checkoutSteps.indexOf(resolvedCurrentStep);
  const skippableStepMap = {
    support_levels: hasSupportLevels,
    reward: hasRewardStep,
  };

  const isStepActive = (step) => {
    return resolvedCurrentStep === step;
  };

  const isStepDone = (step) => {
    return Boolean(stepDoneMap[step]);
  };

  const isStepSkipped = (step) => {
    const stepIndex = checkoutSteps.indexOf(step);

    return Boolean(
      skippableStepMap[step] &&
        !stepDoneMap[step] &&
        stepIndex !== -1 &&
        resolvedCurrentStepIndex > stepIndex
    );
  };

  const handleCheckoutSectionChange = (section) => {
    setCurrentView(section);
  };

  return (
    <>
      <CheckoutPage>
        {donationSuccess && (
          <Redirect
            to={{
              pathname: '/thank-you-page',
              search: `?amount=${Number(donation) || 0}&tips=${
                Number(freiwilliges) || 0
              }&id=${id}${
                project?.title != null &&
                project?.title !== '' &&
                String(project.title) !== 'undefined'
                  ? `&title=${encodeURIComponent(project.title)}`
                  : ''
              }`,
            }}
          />
        )}

        <CheckoutHeader>
          <CheckoutHeaderInner>
            <BackLink
              style={{
                pointerEvents: state?.url || project.url ? 'auto' : 'none',
              }}
              to={isFridayCheckout ? '/freitag' : `/project/${state?.url || project.url}`}
            >
              <ArrowLeftOutlined />
              <span>{isMobile() ? 'Zurück' : 'Zurück zum Projekt'}</span>
            </BackLink>

            <HeaderLogoWrap>
              <img
                src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677e956903bbb028e9d12638_b3f8733e19dbacc0014314b036af41fe_logo-p-500.png"
                alt="commonsplace"
              />
            </HeaderLogoWrap>
          </CheckoutHeaderInner>
        </CheckoutHeader>

        <CheckoutMain>
          <CheckoutInner>
            <ProjectIntroRow>
              <ProjectIntroLeft>
                <Eyebrow>
                  {project?.is_ticketable
                    ? 'Du besuchst das Event'
                    : 'Deine Unterstützung für'}
                </Eyebrow>
                <ProjectTitle>{isFridayCheckout ? 'Freitags-Sadaqah' : project?.title || 'Projekt'}</ProjectTitle>
                {!isFridayCheckout && <AvatarBox
                  isDonationPage={false}
                  isEvent={project?.is_ticketable}
                  size={32}
                  project={project}
                />}
              </ProjectIntroLeft>
              <HeaderRightDecoration>
                <div className="arabic">
                  <BismillahIcon />
                </div>
                <div className="latin">Bismillahi ar-Rahmani ar-Rahim.</div>
              </HeaderRightDecoration>
              <BimisslahMobile>
                <BismillahMobileIcon />
              </BimisslahMobile>
            </ProjectIntroRow>

            <CheckoutContent>
              <CheckoutLeft>
                <SectionCardless ref={supportSectionRef}>
                  <Donation
                    supportLevelSelected={setSupportLevelSelected}
                    setCheckoutSection={handleCheckoutSectionChange}
                    showPaymentForm={showPaymentForm}
                    showOption={showOption}
                    otherOption={otherOption}
                    originalDonation={originalDonation}
                    donation={donation}
                    freiwilliges={freiwilliges}
                    setDonationState={setDonationState}
                    setCurrentViewState={setCurrentView}
                    setShowOptionState={setShowOption}
                    setOtherOptionState={setOtherOption}
                    setFreiwilligesState={setFreiwilligesState}
                    setShowPaymentFormState={setShowPaymentForm}
                    projectId={id}
                    project_name={isFridayCheckout ? 'freitag' : undefined}
                    recurringCheck={project.recurring_check}
                    isTicketable={project.is_ticketable}
                    originalRewardId={originalRewardId}
                    setAllRewards={setAllRewards}
                    setSelectedRewards={setSelectedRewards}
                    allRewards={allRewards}
                    selectedRewards={selectedRewards}
                    formRef={donationFormRef}
                    setAddressRequired={setAddressRequired}
                    handleCLientSecret={handleClientSecret}
                    handelShowModal={handelShowModal}
                    project={project}
                  />
                </SectionCardless>

                <SectionCardless ref={infoSectionRef}>
                  <InfoForm
                    supportLevelId={supportLevelSelected?.id || null}
                    setCurrentViewState={setCurrentView}
                    amount={Number(donation)}
                    tips={Number(freiwilliges)}
                    project={project}
                    projectId={id}
                    donationFormRef={donationFormRef}
                    selectedRewards={selectedRewards}
                    addressRequired={addressRequired}
                    clientSecret={clientSecret}
                    handleCLientSecret={handleClientSecret}
                    onProcessingChange={setCheckoutProcessing}
                    showSummary={
                      <SummaryInMobile>
                        <SummaryCard>
                          <h3>Zusammenfassung</h3>

                          {!project.is_ticketable && (
                            <SummaryRow>
                              <span>
                                Deine Unterstützung
                                <SupportFrequency>
                                  {donationFrequencyLabel}
                                </SupportFrequency>
                              </span>
                              <strong>
                                {formatEuroAmount(donation)} €
                              </strong>
                            </SummaryRow>
                          )}

                          {!!selectedRewards?.length && (
                            <SummaryRow>
                              <span>
                                {project?.is_ticketable ? 'Tickets' : 'Goodies'}
                              </span>
                              <strong>
                                {formatEuroAmount(
                                  selectedRewards.reduce(
                                    (acc, item) =>
                                      acc + getRewardTotalPrice(item),
                                    0
                                  )
                                )}{' '}
                                €
                              </strong>
                            </SummaryRow>
                          )}

                          {!!Number(freiwilliges) && (
                            <SummaryRow>
                              <span>Freiwilliger Beitrag für commonsplace</span>
                              <strong>
                                {formatEuroAmount(freiwilliges)} €
                              </strong>
                            </SummaryRow>
                          )}

                          <SummaryDivider />

                          <SummaryRow className="total">
                            <span>Gesamt</span>
                            <strong>
                              {formatEuroAmount(
                                Number(donation || 0) +
                                Number(freiwilliges || 0) +
                                selectedRewards.reduce(
                                  (acc, item) =>
                                    acc + getRewardTotalPrice(item),
                                  0
                                )
                              )}{' '}
                              €
                            </strong>
                          </SummaryRow>
                        </SummaryCard>
                      </SummaryInMobile>
                    }
                  />
                </SectionCardless>

                {showPaymentForm && (
                  <SectionCardless
                    className="payment-section"
                    ref={paymentSectionRef}
                  >
                    <PaymentSectionTitle>Zahlungsmethode</PaymentSectionTitle>
                    <SecureHint>
                      Dies ist eine sichere und SSL-verschlüsselte Zahlung.
                      Deine Zahlungsdaten sind sicher.
                    </SecureHint>

                    <div className="paymentCard zahlungsmethode">
                      <PaymentCard />
                    </div>
                  </SectionCardless>
                )}
              </CheckoutLeft>

              <CheckoutRight>
                <StickySidebar>
                  <ProgressCard>
                    <ProgressStep
                      $active={isStepActive('support')}
                      $done={isStepDone('support')}
                      $skipped={isStepSkipped('support')}
                    >
                      <div className="icon" />
                      <div className="text">
                        <strong>Deine Unterstützung</strong>
                        <span>Wähle, wie viel du beitragen möchtest</span>
                      </div>
                    </ProgressStep>

                    {hasSupportLevels && (
                      <ProgressStep
                        $active={isStepActive('support_levels')}
                        $done={isStepDone('support_levels')}
                        $skipped={isStepSkipped('support_levels')}
                      >
                        <div className="icon" />
                        <div className="text">
                          <strong>Unterstützungsstufen</strong>
                          <span>Wähle einen vorgeschlagenen Betrag</span>
                        </div>
                      </ProgressStep>
                    )}

                    {hasRhythmusStep && (
                      <ProgressStep
                        $active={isStepActive('rhythmus')}
                        $done={isStepDone('rhythmus')}
                        $skipped={isStepSkipped('rhythmus')}
                      >
                        <div className="icon" />
                        <div className="text">
                          <strong>Rhythmus</strong>
                          <span>Wähle die Häufigkeit deiner Unterstützung</span>
                        </div>
                      </ProgressStep>
                    )}

                    {hasRewardStep && (
                      <ProgressStep
                        $active={isStepActive('reward')}
                        $done={isStepDone('reward')}
                        $skipped={isStepSkipped('reward')}
                      >
                        <div className="icon" />
                        <div className="text">
                          <strong>
                            {project?.is_ticketable ? 'Tickets' : 'Goodies'}
                          </strong>
                          <span>
                            {project?.is_ticketable
                              ? 'Buche optional ein Ticket dazu'
                              : 'Buche optional ein Goodie dazu'}
                          </span>
                        </div>
                      </ProgressStep>
                    )}

                    <ProgressStep
                      $active={isStepActive('info')}
                      $done={isStepDone('info')}
                      $skipped={isStepSkipped('info')}
                    >
                      <div className="icon" />
                      <div className="text">
                        <strong>Persönliche Angaben</strong>
                        <span>
                          {project?.is_ticketable
                            ? 'Personalisiere deine Buchung'
                            : 'Personalisiere deine Unterstützung – hinterlasse eine Botschaft oder gute Absicht'}
                        </span>
                      </div>
                    </ProgressStep>

                    {showPaymentForm && (
                      <ProgressStep
                        $active={isStepActive('payment')}
                        $done={false}
                        $skipped={isStepSkipped('payment')}
                      >
                        <div className="icon" />
                        <div className="text">
                          <strong>Zahlungsmethode</strong>
                          <span>Wähle aus, wie du bezahlen möchtest.</span>
                        </div>
                      </ProgressStep>
                    )}
                  </ProgressCard>

                  <SummaryCard>
                    <h3>Zusammenfassung</h3>

                    {!project.is_ticketable && (
                      <SummaryRow>
                        <span>
                          Deine Unterstützung
                          <SupportFrequency>
                            {donationFrequencyLabel}
                          </SupportFrequency>
                        </span>
                        <strong>{formatEuroAmount(donation)} €</strong>
                      </SummaryRow>
                    )}

                    {!!selectedRewards?.length && (
                      <SummaryRow>
                        <span>
                          {project?.is_ticketable ? 'Tickets' : 'Goodies'}
                        </span>
                        <strong>
                          {formatEuroAmount(
                            selectedRewards.reduce(
                              (acc, item) => acc + getRewardTotalPrice(item),
                              0
                            )
                          )}{' '}
                          €
                        </strong>
                      </SummaryRow>
                    )}

                    {!!Number(freiwilliges) && (
                      <SummaryRow>
                        <span>Freiwilliger Beitrag für commonsplace</span>
                        <strong>{formatEuroAmount(freiwilliges)} €</strong>
                      </SummaryRow>
                    )}

                    <SummaryDivider />

                    <SummaryRow className="total">
                      <span>Gesamt</span>
                      <strong>
                        {formatEuroAmount(
                          Number(donation || 0) +
                          Number(freiwilliges || 0) +
                          selectedRewards.reduce(
                            (acc, item) => acc + getRewardTotalPrice(item),
                            0
                          )
                        )}{' '}
                        €
                      </strong>
                    </SummaryRow>
                  </SummaryCard>
                </StickySidebar>
              </CheckoutRight>
            </CheckoutContent>
          </CheckoutInner>
        </CheckoutMain>

        {/* Mobile sticky summary CTA (only visible on small screens) */}
        {/*<StickyBottomCTA>*/}
        {/*  <div className="summary">*/}
        {/*    <div className="left">*/}
        {/*      <div className="label">Gesamt</div>*/}
        {/*    </div>*/}
        {/*    <div className="right">*/}
        {/*      <div className="amount">*/}
        {/*        {(*/}
        {/*          Number(donation || 0) +*/}
        {/*          Number(freiwilliges || 0) +*/}
        {/*          selectedRewards.reduce(*/}
        {/*            (acc, item) => acc + getRewardTotalPrice(item),*/}
        {/*            0*/}
        {/*          )*/}
        {/*        ).toFixed(2)}{' '}*/}
        {/*        €*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</StickyBottomCTA>*/}
        <CheckoutFooter>
          <CheckoutFooterInner>
            <FooterQuote>
              <h3>
                “Der Beste unter den Menschen ist derjenige, der seinen
                Mitmenschen am nützlichsten ist.”
              </h3>
              <p>– Prophet Muhammed ﷺ</p>
            </FooterQuote>

            <FooterBrand>
              <img
                src="https://cdn.prod.website-files.com/677e8c6239136af36bc65f94/677e956903bbb028e9d12638_b3f8733e19dbacc0014314b036af41fe_logo-p-500.png"
                alt="commonsplace"
              />
              <div>{moment().format('DD.MM.YYYY')}</div>
              <div>{getIslamicDate().hijri}</div>
            </FooterBrand>
          </CheckoutFooterInner>

          <CheckoutFooterBottom>
            © 2026 commonsplace. Alle Rechte vorbehalten.
          </CheckoutFooterBottom>
        </CheckoutFooter>

        {checkoutProcessing && (
          <FullPageLoading>
            <Spin size="large" />
            <span>Zahlung wird verarbeitet...</span>
          </FullPageLoading>
        )}
      </CheckoutPage>

      <Modal showModal={showModal} handelCloseModal={handelCloseModal}>
        <DankeModal>
          <p>
            Unterstütze uns, um deine Community zu unterstützen! Wir sind ein
            junges Team, das den Traum hat, etwas Positives für die muslimische
            Gemeinschaft in Deutschland zu bewirken. Wir sorgen mit dem Geist
            der Gemeinschaft dafür, dass Muslime in Deutschland mit großartigen
            Ideen zum Wohl der Gesamtgesellschaft beitragen. Auch commonsplace
            finanziert sich durch die Gemeinschaft - Crowdfinanzierung: Wenn Du
            ein Projekt auf commonsplace unterstützt, kannst du auch uns mit
            einem freiwilligen Beitrag behilflich sein. Dank Deines Beitrags
            können wir die Plattform bereitstellen, Projekte mit aufbauen und so
            die Community unterstützen, Großes zu erreichen. Ohne Dich gibt es
            uns nicht! Vielen Dank, dass Du Teil unserer Vision bist.
          </p>

          <PrimaryButton onClick={handelCloseModal}>
            Teil der Vision werden
          </PrimaryButton>
        </DankeModal>
      </Modal>
    </>
  );
}

DonationV2Page.propTypes = {
  showProject: PropTypes.func.isRequired,
  updateDraft: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object,
  donationSuccess: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  donationType: PropTypes.number,
};

const mapStateToProps = (state) => {
  const { donationSuccess, donationType } = state.payment;
  const { project } = state.projects;

  return {
    donationSuccess,
    donationType,
    project,
  };
};

const mapDispatchToProps = (dispatch) => ({
  showProject: (url) => dispatch(ProjectsActions.showProject(url)),
  updateDraft: (id, project) =>
    dispatch(ProjectsActions.updateDraft(id, project)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DonationV2Page)
);

const SummaryInMobile = styled.div`
  display: none;
  @media (max-width: 1100px) {
    margin-top: 12px;
    display: block;
  }
`

const CheckoutPage = styled.div`
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  overflow-x: clip;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const FullPageLoading = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(2px);
  color: #414651;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
`;

const CheckoutHeader = styled.header`
  height: 80px;

  background: #fff;

  border-bottom: 1px solid #e9eaeb;

  display: flex;

  align-items: center;

  justify-content: center;
  
  @media ${device.allMobile} {
    border-bottom:none;
  }
`;

const CheckoutHeaderInner = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  max-width: 1280px;
  height: 100%;
  margin: 0 auto;
  padding: 0 32px;

  display: flex;
  align-items: center;

  @media ${device.allMobile} {
    padding: 0 16px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: #535862;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  span {
    /* Text */

    /* Text md/Semibold */
    font-family: 'Inter';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #535862;
  }

  @media ${device.allMobile} {
    font-size: 14px;
    /* Text md/Semibold */
    font-family: 'Inter';
    font-style: normal;
    font-weight: 600;
    color: #535862;
  }
`;

const HeaderLogoWrap = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 140px;
    height: auto;
    display: block;
  }

  @media ${device.allMobile} {
    img {
      width: 110px;
    }
  }
`;

const HeaderRightDecoration = styled.div`
  justify-self: end;
  text-align: right;
  color: #717680;

  .arabic {
    font-size: 20px;
    line-height: 1.1;
    opacity: 0.7;
  }

  .latin {
    font-size: 14px;
    margin-top: 4px;
  }

  @media ${device.allMobile} {
    display: none;
  }
`;

const BimisslahMobile = styled.div`
  display: none;

  @media ${device.allMobile} {
    position: absolute;
    left: 0;
    right: 0;
    color: #a4a7ae;
    display: block;
      opacity: 0.1;
    text-align: center;
    margin-top: -54px;
    width: auto;
    overflow: hidden;
  }
`;

const CheckoutMain = styled.main`
  flex: 1;
  padding: 24px 0 64px;
    @media ${device.allMobile} {
      padding: 24px 0 0px;
      
    }
`;

const CheckoutInner = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;

  @media ${device.allMobile} {
    width: auto;
    max-width: none;
    margin-inline: 0;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const ProjectIntroRow = styled.div`
  position: relative;
  min-width: 0;
  margin-bottom: 24px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;

  @media ${device.allMobile} {
    margin-bottom: 16px;
  }
`;

const ProjectIntroLeft = styled.div`
  display: flex;
  flex-direction: column;
  @media ${device.allMobile} {
    margin-top:0px;
  }
`;

const Eyebrow = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  line-height: 28px;
  color: #535862;

  @media ${device.allMobile} {
    font-size: 16px;
    line-height: 20px;
  }
`;

const ProjectTitle = styled.h1`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  line-height: 40px;
  font-weight: 600;
  color: #181d27;
  margin-bottom:4px;

  @media ${device.allMobile} {
    font-size: 16px;
    line-height: 24px;
      font-weight: 600;
  }
`;

const ProjectMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #181d27;
  font-weight: 600;
`;

const OrgAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  object-fit: cover;
  background: #eee;
`;

const CheckoutContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 702px) 450px;
  gap: 64px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StickyBottomCTA = styled.div`
  display: none;

  @media (max-width: 1100px) {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 60;
    background: #ffffff;
    box-shadow: 0px -4px 6px -1px rgba(10, 13, 18, 0.1), 0px 0px 4px -2px rgba(10, 13, 18, 0.06);
    padding: 16px 16px 32px;
  }

  .summary {
    max-width: 1280px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .label {
    /* Text sm/Regular */
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px; /* or 143% */
    display: flex;
    align-items: center;
    color: #181D27;
  }

  .sub {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #535862;
    margin-top: 4px;
  }

  .amount {
    /* Amount */
    /* Text sm/Regular */
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px; /* identical to box height, or 143% */
    display: flex;
    align-items: center;
    color: #181D27;
  }
`;

const CheckoutLeft = styled.div`
  min-width: 0;
`;

const CheckoutRight = styled.aside`
  min-width: 0;
  align-self: start;
  position: sticky;
  top: 24px;
`;

const StickySidebar = styled.div`
  

  display: flex;
  flex-direction: column;
  gap: 32px;
  align-self: start;

  @media (max-width: 1100px) {
    position: static;
    display: none;
  }
  
`;

const SectionCardless = styled.section`
  margin-bottom: 32px;
`;

const PaymentSectionTitle = styled.h2`
  margin: 0 0 12px;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  line-height: 28px;
  font-weight: 600;
  color: #181d27;
`;

const SecureHint = styled.div`
  min-height: 46px;
  background: #def7ec;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #181d27;
`;

const ProgressCard = styled.div`
  background: #fff;
  border: 1px solid #d5d7da;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  @media (max-width: 1100px) {
    display: none;
  }
`;

const ProgressStep = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 12px;
  position: relative;
  padding-bottom: 20px;

  &:not(:last-of-type)::after {
    content: '';
    position: absolute;
    left: 11px;
    top: 28px;
    width: 2px;
    bottom: 4px;
    background: ${({ $active, $done, $skipped }) =>
      $done || $active ? '#669f2a' : $skipped ? '#d5d7da' : '#e9eaeb'};
    border-radius: 2px;
  }

  .icon {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: ${({ $done, $active, $skipped }) =>
      $done
        ? '#9dbc60'
        : $active
        ? '#9dbc60'
        : $skipped
        ? '#f5f5f5'
        : '#fafafa'};
    border: ${({ $done, $active, $skipped }) =>
      $done
        ? 'none'
        : $active
        ? '4px solid #669f2a'
        : $skipped
        ? '1.5px solid #a4a7ae'
        : '1.5px solid #e9eaeb'};
    box-shadow: ${({ $active, $done, $skipped }) =>
      !$done && !$skipped && $active ? '0 0 0 2px #fff' : 'none'};
    position: relative;
  }

  .icon::after {
    content: '';

    position: absolute;

    ${({ $done, $active, $skipped }) =>
      $done || $skipped
        ? `

        width: 10px;

        height: 6px;

        border-left: 2px solid ${$skipped ? '#717680' : '#fff'};

        border-bottom: 2px solid ${$skipped ? '#717680' : '#fff'};

        background: transparent;

        border-radius: 0;

        top: 46%;

        left: 50%;

        transform: translate(-50%, -50%) rotate(-45deg);

      `
        : `

        width: 8px;

        height: 8px;

        border-radius: 999px;

        background: ${$active ? '#fff' : '#d5d7da'};

        top: 50%;

        left: 50%;

        transform: translate(-50%, -50%);

      `}
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 2px;
  }

  .text strong {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: ${({ $active }) => ($active ? '#3f621a' : '#414651')};
  }

  .text span {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: ${({ $active }) => ($active ? '#3f621a' : '#535862')};
  }
`;

const SummaryCard = styled.div`
  background: #fafafa;
  border: 1px solid #d5d7da;
  border-radius: 12px;
  padding: 24px;

  h3 {
    margin: 0 0 8px !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 18px !important;
    line-height: 28px !important;
    font-weight: 700 !important;
    color: #181d27 !important;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  line-height: 28px;
  color: #181d27;

  span {
    max-width: 301px;

    font-family: 'Helvetica';
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 28px;
    /* identical to box height, or 157% */
    display: flex;
    align-items: center;

    color: rgba(0, 0, 0, 0.85);

    @media ${device.allMobile} {
      /* Text sm/Regular */
      font-family: 'Inter';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      /* identical to box height, or 143% */
      display: flex;
      align-items: center;
      color: #181d27;
    }
  }

  span,
  strong {
    font-weight: 400;
  }
  strong {
    min-width: 61px;
    text-align: right;
  }

  &.total {
    margin-bottom: 0;

    span,
    strong {
      font-weight: 400;
    }
  }

  @media ${device.allMobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const SupportFrequency = styled.span`
  display: inline-flex !important;
  align-items: center;
  margin-left: 6px;
  max-width: none !important;
  font-family: 'Inter', sans-serif !important;
  font-style: normal;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  color: #717680 !important;
`;

const SummaryDivider = styled.div`
  height: 1px;
  background: #ccc;
  margin: 8px 0 8px;
`;

const CheckoutFooter = styled.footer`
  background: #f5fbee;
  margin-top: 40px;
  padding: 64px 0 48px;
  @media ${device.allMobile} {
   margin-top: 0;
  }
`;

const CheckoutFooterInner = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  justify-content: space-between;
  gap: 32px;
  align-items: flex-start;

  @media ${device.allMobile} {
    padding: 0 16px;
    flex-direction: column;
  }
`;

const FooterQuote = styled.div`
  max-width: 768px;

  h3 {
    margin: 0 0 16px;
    font-family: 'Inter', sans-serif;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    color: #181d27;
  }

  p {
    margin: 0;
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    line-height: 28px;
    color: #535862;
  }
`;

const FooterBrand = styled.div`
  min-width: 220px;
  text-align: right;
  color: #535862;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;

  img {
    width: 160px;
    margin-bottom: 16px;
  }

  @media ${device.allMobile} {
    text-align: left;
  }
`;

const CheckoutFooterBottom = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1280px;
  margin: 32px auto 0;
  padding: 32px 32px 0;
  border-top: 1px solid #e9eaeb;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #717680;

  @media ${device.allMobile} {
    padding: 24px 16px 0;
  }
`;
